import type { LocalStorageIdentity } from '../generated/localStorageSchema.generated';
import type {
  DictionaryEnsureRequest,
  DictionaryTokenizeRequest,
  DictionaryWorkerResponse,
} from '../dictionaryTypes';
import type { EngineEpoch } from '../localStorageOwnerTypes';
import type { StorageCompatibilityIssue } from './identity';
import type {
  MutationAuthorMetadata,
  MutationIntentName,
} from './mutations';
import type { NamedReadName } from './requests';
import type { ElectricSyncStatus } from './electricRuntime';
import type { AccountLoadStatus, AccountOperation } from './accountRuntime';

export const localStorageBroadcastChannelName = 'alfrec-local-storage';

export type LocalStorageBroadcastMessage = {
  type: 'storage-update-required';
  requiredEpoch: number;
};

export type OpenUserRequest = {
  type: 'open-user';
  requestId: string;
  userId: string;
};

export type InspectStorageRequest = {
  type: 'inspect-storage';
  requestId: string;
};

export type ExportStorageFileRequest = {
  type: 'export-storage-file';
  requestId: string;
  filename: string;
};

export type DebugSetIdentityRequest = {
  type: 'debug-set-identity';
  requestId: string;
  coreGeneration: number;
};

export type DebugResetUserDatabaseRequest = {
  type: 'debug-reset-user-database';
  requestId: string;
};

export type DebugCommitStorageRequest = {
  type: 'debug-commit-storage';
  requestId: string;
  count: number;
};

export type ReadRequest = {
  type: 'read';
  requestId: string;
  request: NamedReadName;
  params: unknown;
};

export type MutateRequest = {
  type: 'mutate';
  requestId: string;
  intent: MutationIntentName;
  payload: unknown;
  authoredAt: string;
  metadata: MutationAuthorMetadata;
  dependencies?: string[];
};

export type AccountRequest = {
  type: 'account';
  requestId: string;
  operation: AccountOperation;
};

export type AuthTokenRequest = {
  type: 'auth-token';
  accessToken?: string;
};

export type NetworkOnlineRequest = {
  type: 'network-online';
};

export type RetrySyncIssuesRequest = {
  type: 'retry-sync-issues';
  requestId: string;
};

export type EngineRequest =
  | DictionaryEnsureRequest
  | DictionaryTokenizeRequest
  | OpenUserRequest
  | InspectStorageRequest
  | ExportStorageFileRequest
  | DebugSetIdentityRequest
  | DebugResetUserDatabaseRequest
  | DebugCommitStorageRequest
  | ReadRequest
  | MutateRequest
  | AccountRequest
  | AuthTokenRequest
  | NetworkOnlineRequest
  | RetrySyncIssuesRequest;

export type EngineHelloResponse = {
  type: 'engine-hello';
  engineEpoch: EngineEpoch;
  storageRuntimeEpoch: number;
};

export type UserReadyResponse = {
  type: 'user-ready';
  engineEpoch: EngineEpoch;
  requestId?: string;
  identity: LocalStorageIdentity;
};

export type UserHydratingResponse = {
  type: 'user-hydrating';
  engineEpoch: EngineEpoch;
  requestId?: string;
  identity: LocalStorageIdentity;
  status: ElectricSyncStatus;
  account: AccountLoadStatus;
};

export type StorageGateResponse = {
  type: 'storage-gate';
  engineEpoch: EngineEpoch;
  requestId?: string;
  issue: StorageCompatibilityIssue;
  title: string;
  message: string;
  telemetryReason: string;
};

export type AccountTransitionRequiredResponse = {
  type: 'account-transition-required';
  engineEpoch: EngineEpoch;
  boundUserId: string;
  requestedUserId: string;
};

export type InspectStorageAttachedDatabase = {
  name: string;
  file: string;
};

export type InspectStorageResultResponse = {
  type: 'inspect-storage-result';
  engineEpoch: EngineEpoch;
  requestId: string;
  identity?: LocalStorageIdentity;
  ownership: ReadonlyArray<{ tableName: string; ownership: string }>;
  journal: ReadonlyArray<{ migrationId: string; result: string }>;
  attachedDatabases: InspectStorageAttachedDatabase[];
  quickCheck: string;
  poolFiles: string[];
  error?: string;
};

export type ExportStorageFileResultResponse = {
  type: 'export-storage-file-result';
  engineEpoch: EngineEpoch;
  requestId: string;
  filename: string;
  bytes?: Uint8Array;
  error?: string;
};

export type ReadResultResponse = {
  type: 'read-result';
  engineEpoch: EngineEpoch;
  databaseRevision: number;
  requestId: string;
  request: NamedReadName;
  result?: unknown;
  error?: string;
};

export type InvalidateResponse = {
  type: 'invalidate';
  engineEpoch: EngineEpoch;
  databaseRevision: number;
};

export type MutationResultResponse = {
  type: 'mutation-result';
  engineEpoch: EngineEpoch;
  databaseRevision: number;
  requestId: string;
  mutationId?: string;
  clientSeq?: number;
  error?: string;
};

export type AccountResultResponse = {
  type: 'account-result';
  engineEpoch: EngineEpoch;
  requestId: string;
  error?: string;
};

export type RetrySyncIssuesResultResponse = {
  type: 'retry-sync-issues-result';
  engineEpoch: EngineEpoch;
  requestId: string;
  retriedCount?: number;
  error?: string;
};

export type AuthRequiredResponse = {
  type: 'auth-required';
  engineEpoch: EngineEpoch;
  message: string;
};

export type ElectricSyncStatusResponse = {
  type: 'electric-sync-status';
  engineEpoch: EngineEpoch;
  status: ElectricSyncStatus;
};

export type VersionUpdateRequiredResponse = {
  type: 'version-update-required';
  engineEpoch: EngineEpoch;
  code: 'client_build_retired' | 'read_contract_retired';
  message: string;
  minimumBuild?: number;
  minimumReadContract?: number;
};

export type EngineResponse =
  | DictionaryWorkerResponse
  | EngineHelloResponse
  | UserReadyResponse
  | UserHydratingResponse
  | StorageGateResponse
  | AccountTransitionRequiredResponse
  | InspectStorageResultResponse
  | ExportStorageFileResultResponse
  | ReadResultResponse
  | InvalidateResponse
  | MutationResultResponse
  | AccountResultResponse
  | RetrySyncIssuesResultResponse
  | AuthRequiredResponse
  | ElectricSyncStatusResponse
  | VersionUpdateRequiredResponse;
